import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { Alert, CircularProgress } from '@mui/material';

import navOnlineService from '../../service/navOnline.service';
import NavOnlineNotificationRecipients from './NavOnlineNotificationRecipients';
import { InputUI, CheckboxUI, SelectUI, ButtonUI } from '../Interface';

const fieldGroups = [
  {
    title: 'Technikai felhasználó',
    fields: [
      { name: 'login', label: 'Felhasználónév', max: 100, required: true },
      { name: 'password', label: 'Jelszó', secret: true, required: true },
      { name: 'signingKey', label: 'XML aláírókulcs', secret: true, required: true },
      { name: 'exchangeKey', label: 'XML cserekulcs', secret: true, required: true },
      { name: 'taxNumber', label: 'Adózó adószáma', max: 20, required: true },
    ],
  },
  {
    title: 'Számlázóprogram adatai',
    readOnly: true,
    fields: [
      { name: 'softwareId', label: 'Szoftverazonosító', max: 18, required: true },
      { name: 'softwareName', label: 'Szoftver neve', max: 100, required: true },
      { name: 'softwareMainVersion', label: 'Szoftver főverziója', max: 15, required: true },
      { name: 'softwareDevName', label: 'Fejlesztő neve', max: 100, required: true },
      { name: 'softwareDevContact', label: 'Fejlesztő elérhetősége', max: 255, required: true },
      { name: 'softwareDevCountryCode', label: 'Fejlesztő országkódja', max: 2 },
      { name: 'softwareDevTaxNumber', label: 'Fejlesztő adószáma', max: 20 },
    ],
  },
];

export const toNavConfigurationValues = (configuration = {}) => {
  const values = {
    enabled: configuration.enabled ?? false,
    environment: configuration.environment ?? 'TEST',
    softwareOperation: configuration.softwareOperation ?? 'LOCAL_SOFTWARE',
  };
  fieldGroups.forEach(({ fields }) => fields.forEach(({ name, secret }) => {
    values[name] = secret ? '' : configuration[name] ?? '';
  }));
  return values;
};

export const toNavConfigurationPayload = (values) => {
  const payload = {
    enabled: values.enabled,
    environment: values.environment,
  };
  fieldGroups.filter(({ readOnly }) => !readOnly).forEach(({ fields }) => fields.forEach(({ name, secret }) => {
    if (secret) {
      if (values[name]) payload[name] = values[name];
    } else {
      payload[name] = values[name].trim() || null;
    }
  }));
  return payload;
};

export const validateNavConfiguration = (values, savedConfiguration = {}) => {
  const errors = {};
  fieldGroups.filter(({ readOnly }) => !readOnly).forEach(({ fields }) => fields.forEach(({ name, required, max, secret }) => {
    const value = values[name];
    if (values.enabled && required && !value.trim() && !(secret && savedConfiguration[name])) {
      errors[name] = 'Kötelező mező.';
    } else if (max && value.trim().length > max) {
      errors[name] = `Legfeljebb ${max} karakter adható meg.`;
    }
  }));
  if (!['TEST', 'PRODUCTION'].includes(values.environment)) errors.environment = 'Válassz környezetet.';
  if (values.taxNumber && !/^\d{8}(?:-\d-\d{2})?$/.test(values.taxNumber.trim())) {
    errors.taxNumber = '8 számjegy vagy teljes magyar adószám szükséges (12345678-1-12).';
  }
  if (values.exchangeKey && new TextEncoder().encode(values.exchangeKey).length !== 16) {
    errors.exchangeKey = 'A cserekulcsnak pontosan 16 bájt hosszúnak kell lennie.';
  }
  return errors;
};

const errorMessage = (error, fallback) => error?.response?.data?.error?.message || fallback;

const NavOnlineSettingFields = ({ canEdit = false }) => {
  const [savedConfiguration, setSavedConfiguration] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [testing, setTesting] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const mounted = useRef(false);
  const busy = useRef(false);

  const form = useFormik({
    initialValues: toNavConfigurationValues(),
    validate: (values) => validateNavConfiguration(values, savedConfiguration),
    validateOnChange: false,
    onSubmit: async (values) => {
      if (!canEdit || loading || loadError || busy.current) return;
      busy.current = true;
      setFeedback(null);
      try {
        const configuration = await navOnlineService.saveConfiguration(toNavConfigurationPayload(values));
        if (!mounted.current) return;
        setSavedConfiguration(configuration);
        form.resetForm({ values: toNavConfigurationValues(configuration) });
        setFeedback({ severity: 'success', message: 'A NAV-beállítások mentése sikeres.' });
      } catch (error) {
        if (mounted.current) setFeedback({ severity: 'error', message: errorMessage(error, 'A NAV-beállítások mentése sikertelen.') });
      } finally {
        busy.current = false;
      }
    },
  });

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    setLoading(true);
    setLoadError('');
    navOnlineService.getConfiguration(controller.signal)
      .then((configuration) => {
        if (controller.signal.aborted) return;
        setSavedConfiguration(configuration ?? {});
        form.resetForm({ values: toNavConfigurationValues(configuration ?? {}) });
      })
      .catch((error) => {
        if (!controller.signal.aborted) setLoadError(errorMessage(error, 'A NAV-beállítások betöltése sikertelen.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      mounted.current = false;
      controller.abort();
    };
    // A form értékeinek változása nem indíthat újabb betöltést.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAttempt]);

  const testConnection = async () => {
    if (!canEdit || form.dirty || form.isSubmitting || busy.current) return;
    busy.current = true;
    setTesting(true);
    setFeedback(null);
    try {
      const result = await navOnlineService.testConnection();
      if (!mounted.current) return;
      setFeedback({
        severity: result.authenticated ? 'success' : 'error',
        message: result.authenticated
          ? `Sikeres NAV-kapcsolat (${result.environment === 'PRODUCTION' ? 'éles' : 'teszt'} környezet).`
          : [result.message || 'A NAV-hitelesítés sikertelen.', result.navErrorCode].filter(Boolean).join(' '),
      });
    } catch (error) {
      if (mounted.current) setFeedback({ severity: 'error', message: errorMessage(error, 'A NAV-kapcsolat ellenőrzése sikertelen.') });
    } finally {
      busy.current = false;
      if (mounted.current) setTesting(false);
    }
  };

  if (loading) return <div className="mx-4 my-5 flex items-center gap-3"><CircularProgress size={24} />NAV-beállítások betöltése...</div>;
  if (loadError) return (
    <div className="mx-4 my-5">
      <Alert severity="error">{loadError}</Alert>
      <ButtonUI type="button" text="Újrapróbálás" className="bg-primary mt-3" onClick={() => setLoadAttempt((value) => value + 1)} />
    </div>
  );

  const disabled = !canEdit || form.isSubmitting || testing;
  const fieldError = (name) => form.touched[name] ? form.errors[name] : undefined;

  return (
    <div className="mx-4 mb-5" onKeyDown={(event) => {
      if (event.key === 'Enter' && event.target.tagName === 'INPUT') event.preventDefault();
    }}>
      <Alert severity="info" className="mb-4">
        A NAV-beállítások külön menthetők. A már beállított jelszó és kulcsok üresen hagyva megmaradnak.
      </Alert>
      {feedback && <Alert severity={feedback.severity} className="mb-4">{feedback.message}</Alert>}
      <div className="grid grid-cols-6 gap-x-4 gap-y-3">
        <div className="col-span-6">
          <CheckboxUI name="enabled" label="NAV-adatszolgáltatás engedélyezése" checked={form.values.enabled}
            onChange={form.handleChange} disabled={disabled} />
        </div>
        <div className="col-span-6 md:col-span-3 xl:col-span-2">
          <SelectUI name="environment" label="Környezet" value={form.values.environment}
            option={[{ value: 'TEST', label: 'Teszt' }, { value: 'PRODUCTION', label: 'Éles' }]}
            onChange={form.handleChange} onBlur={form.handleBlur} disabled={disabled} helperText={fieldError('environment')} />
        </div>
        {fieldGroups.map(({ title, fields, readOnly }) => (
          <React.Fragment key={title}>
            <div className="col-span-6 text-sm font-medium text-labelColor mt-4">{title}</div>
            {fields.map(({ name, label, secret, max }) => (
              <div key={name} className="col-span-6 md:col-span-3 xl:col-span-2">
                <InputUI name={name} label={label} type={secret ? 'password' : 'text'} variant="standard" fullWidth
                  value={form.values[name]} onChange={form.handleChange} onBlur={form.handleBlur} disabled={disabled || readOnly}
                  autoComplete={secret ? 'new-password' : 'off'} inputProps={{ maxLength: max }}
                  InputLabelProps={{ shrink: true }} error={Boolean(fieldError(name))}
                  placeholder={secret && savedConfiguration[name] ? 'Már beállítva' : ''}
                  helperText={fieldError(name) || (secret && savedConfiguration[name] ? 'Üresen hagyva megmarad.' : '')} />
              </div>
            ))}
          </React.Fragment>
        ))}
        <div className="col-span-6 md:col-span-3 xl:col-span-2">
          <SelectUI name="softwareOperation" label="Szoftver működési módja" value={form.values.softwareOperation}
            option={[{ value: 'LOCAL_SOFTWARE', label: 'Helyi szoftver' }, { value: 'ONLINE_SERVICE', label: 'Online szolgáltatás' }]}
            onChange={form.handleChange} onBlur={form.handleBlur} disabled helperText={fieldError('softwareOperation')} />
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <ButtonUI type="button" text={form.isSubmitting ? 'Mentés...' : 'NAV-beállítások mentése'} className="bg-primary"
          disabled={disabled || !form.dirty} onClick={() => form.submitForm()} />
        <ButtonUI type="button" text="Mégse" className="bg-primary" disabled={disabled || !form.dirty}
          onClick={() => { form.resetForm(); setFeedback(null); }} />
        <ButtonUI type="button" text={testing ? 'Ellenőrzés...' : 'Kapcsolat ellenőrzése'} className="bg-primary"
          disabled={disabled || form.dirty || !savedConfiguration.id} onClick={testConnection} />
      </div>
      {form.dirty && <p className="mt-3 text-sm text-labelColor">A kapcsolat ellenőrzése előtt mentsd a NAV-beállításokat.</p>}
      <NavOnlineNotificationRecipients canEdit={canEdit} />
    </div>
  );
};

export default NavOnlineSettingFields;
